import { useState, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { useTranslation, withTranslation } from "react-i18next";
import debug from "@plannel/utils/debug-util";

import { Box, CardMedia, Typography } from "@mui/material";
import AppCard from "@plannel/components/ui/AppCard";
import { green } from "@mui/material/colors";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { SwiperSlide } from "swiper/react";
import "@plannel/pages/demand-plan/demand-management/swiper-scss/swiper.scss";
import "@plannel/pages/demand-plan/demand-management/swiper-scss/swiper-navigation.scss";
import "@plannel/pages/demand-plan/demand-management/swiper-scss/swiper-pagination.scss";

import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import RatioBarCell from "@plannel/components/aggrid/RatioBarCell";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import numberUtil from "@plannel/utils/number-util";
import dateUtils from "@plannel/utils/date-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import planVersionService from "@plannel/services/data/plan-version-service";
import userService from "@plannel/services/system/user-service";

import { includes, isEmpty, isEqual, isNil, isUndefined, size } from "lodash";
import Snackbar from "@plannel/components/Snackbar";
import ScenarioReleaseModal from "@plannel/components/modal/ScenarioReleaseModal";
import UnconfirmedScenarioNotice, { isUnconfirmedScenarioVersion } from "@plannel/components/UnconfirmedScenarioNotice";
import Dialog from "@plannel/components/Dialog";
import { LockButton, UnlockButton } from "@plannel/components/ActionIconButton";
import mpScenarioComparsionService from "@plannel/services/mp/mp-scenario-comparion-service";
import comparisonComponent from "@plannel/components/layout/ComparisonContainer";
import { ComparisonReleaseBar, SelectableComparison } from "@plannel/components/layout/ComparisonSelection";
import { getLastBucketUnit, getPrimaryBucketUnit, normalizeBucketUnit } from "@plannel/pages/master-plan/utils/mp-bucket-util";

const { roles, versionStatusCode } = CodeUtil;
const { DP, RP, MP } = CodeUtil.module;
const { RELEASED, SCENARIO, SIMULATION, PLANNED, RELEASING } = versionStatusCode;
const { DAY } = CodeUtil.buckets;

// MP 는 단일 BUCKET_UNIT 대신 TIME_BUCKETS 를 쓰고, 값도 week / partialWeek 가 섞이며 대소문자도 일정하지 않다.
// 레거시 BUCKET_UNIT 을 정확 비교하면 그 차이에서 주 단위 판정을 놓치고, 그러면 주별 base date 가
// "yyyy-MM" 으로 포맷되면서 같은 달끼리 같은 키가 되어 마지막 주 값만 남는다(차트가 비어 보이는 원인).
const getVersionBucketUnit = planConfigObj =>
  getPrimaryBucketUnit(planConfigObj) ?? normalizeBucketUnit(planConfigObj?.BUCKET_UNIT);

// 계획 기간 문자열. MP 는 세그먼트마다 버킷이 달라(앞 주 / 뒤 월 등) 시작·종료를 각자의 버킷 단위로 표기한다.
const getPlanPeriod = planConfigObj => {
  const { PLAN_START_DATE, PLAN_END_DATE } = planConfigObj ?? {};
  if (!PLAN_START_DATE || !PLAN_END_DATE) {
    return "";
  }

  const startUnit = getVersionBucketUnit(planConfigObj);
  const endUnit = getLastBucketUnit(planConfigObj) ?? startUnit;

  return `${dateUtils.headerDateFormatter(PLAN_START_DATE, startUnit)} ~ ${dateUtils.headerDateFormatter(PLAN_END_DATE, endUnit)}`;
};
// planConfigObj 가 가리키는 상위 모듈(DP/RP) 버전 찾기 — 릴리즈 전 경고 판단에 쓴다.
const findVersionById = (versions, versionId) => {
  if (!versionId || isEmpty(versions)) {
    return null;
  }
  return versions.find(version => isEqual(version.id, versionId)) ?? null;
};

const PERIOD = "period";
const { DEMANDQTY, SUPPLYQTY, PRODUCTIONQTY, ONHANDQTY } = {
  DEMANDQTY: "demandQty",
  SUPPLYQTY: "supplyQty",
  PRODUCTIONQTY: "productionQty",
  ONHANDQTY: "onHandQty",
};
const { snackBarMessageColor } = ColorUtil;
// const userInfo = axiosUtil.jsonbigParse(localStorage.getItem("user"));

const TitleCom = ({ planVersion, retrieveDeleteFlg, isReleasing }) => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          height: "55px",
          flexShrink: 0,
          alignItems: "flex-end",
          justifyContent: "center",
          borderRadius: "5px",
        }}>
        <LeaderboardIcon
          fontSize={"large"}
          sx={{ mb: 0.2 }}
          color={"success"}
        />
        <Typography
          variant="subtitle1"
          sx={{ pl: 1, textAlign: "center", fontWeight: "500" }}>
          {planVersion.name}
        </Typography>
      </Box>
      <Box sx={{ position: "absolute", top: "37px", right: "15px" }}>
        {planVersion?.statusCd === RELEASED ? null : planVersion?.delFlg ? (
          <UnlockButton
            label="lock"
            disabled={isEqual(planVersion?.statusCd, RELEASING) || isReleasing}
            onClick={() => retrieveDeleteFlg(planVersion)}
          />
        ) : (
          <LockButton
            disabled={isEqual(planVersion?.statusCd, RELEASING) || isReleasing}
            label="unlock"
            onClick={() => retrieveDeleteFlg(planVersion)}
          />
        )}
      </Box>
    </>
  );
};

const Comparison = ({ title, symbol, data, index, retrieveDeleteFlg, dataMax, isReleasing }) => {
  const { t } = useTranslation();
  const { mpVersion: version, headers: baseDates, weekHeaders } = data;
  const [gridResultData, setGridResultData] = useState([]);

  const bucketUnit = getVersionBucketUnit(version?.planConfigObj);
  const paramHeaders = CodeUtil.isWeekBucket(bucketUnit) ? weekHeaders : baseDates;
  // 세로 배치를 flex 로 둬서 그리드가 남는 높이를 전부 먹게 한다.
  // 카드마다 있던 릴리즈 버튼이 빠진 자리를 고정 calc 로 두면 카드 아래쪽에 빈 공간이 남는다.
  const mainStyle = {
    // 100vh 계산을 쓰지 않는다 — 실제 가용 높이는 #tab-body 가 정하고 뷰포트보다 작다(App.scss 주석).
    height: "100%",
    // 전역 border-box 가 없는 앱이다. content-box 로 두면 위아래 10px 패딩이 높이에 더해져
    // 카드가 제 칸보다 20px 커지고 맨 아래 상태 배지가 잘린다.
    boxSizing: "border-box",
    p: "10px 0",
    pr: 0.5,
    display: "flex",
    flexDirection: "column",
  };
  const [chartLabel, setChartLabel] = useState([]);
  const [chartData, setChartData] = useState([]);
  const chartRawRef = useRef(null); // 마지막 차트 입력 — 언어 변경 시 재조립용
  const chartSeries = [
    {
      field: DEMANDQTY,
      text: t(DEMANDQTY),
      type: "bar",
      typeOptions: { fill: ColorUtil.colorSet(0), barSize: 15, mr: 1 },
      orientation: "left",
      groupName: t("qty"),
    },
    {
      field: SUPPLYQTY,
      text: t(SUPPLYQTY),
      type: "bar",
      typeOptions: { fill: ColorUtil.colorSet(1), barSize: 15 },
      orientation: "left",
      groupName: t("qty"),
    },
  ];

  const pivotConfig = {
    groupKey: "qty",
    chartSeries,
    include: chartLabel
  };

  const settingChartDatas = (datas, baseDates) => {
    const chartData = [];
    const dateFormats = [];

    chartRawRef.current = { datas, baseDates };

    if (Array.isArray(baseDates)) {
      // 일 단위만 "yyyy-MM-dd". 주 단위는 위에서 weekHeaders 로 빠지므로 여기 오지 않는다.
      const dateFormat = isEqual(getVersionBucketUnit(datas?.mpVersion?.planConfigObj), DAY) ? "yyyy-MM-dd" : "yyyy-MM";
      for (const date of baseDates) {
        const baseDate = dateUtils.formatDate(date, dateFormat);
        dateFormats.push(baseDate);
      }
    } else {
      if (!isEmpty(baseDates)) {
        const sortHeaders = Object.keys(baseDates).sort();
        for (const date of sortHeaders) {
          dateFormats.push(date);
        }
      }
    }

    if (!isEmpty(datas) && !isEmpty(baseDates)) {
      for (const [key, value] of Object.entries(datas)) {
        const qtyData = {};
        if (isEqual(key, DEMANDQTY) || isEqual(key, SUPPLYQTY)) {
          qtyData.qty = t(key);
          for (let i = 0; i < dateFormats.length; i++) {
            qtyData[dateFormats[i]] = value[i];
          }
          chartData.push(qtyData);
        }
      }
    }
    setChartData(chartData);
    setChartLabel(dateFormats);
  };

  /*
   * 언어 변경 — 차트 데이터의 qty(groupKey) 값은 t() 로 번역된 문자열이고, PivotChartComposed 는
   * 그 값을 시리즈의 text 와 맞춰(chartSeries.find(text === dataKey)) 무엇을 그릴지 고른다.
   * 시리즈만 새 언어가 되면 짝이 깨져 그래프가 사라지므로, 데이터도 같이 다시 만든다(재조회 없음).
   */
  useEffect(() => {
    const raw = chartRawRef.current;
    if (isNil(raw)) {
      return;
    }
    settingChartDatas(raw.datas, raw.baseDates);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const setCurrency = value => {
    if (value) {
      const number = Number(value);
      if (number > 0) {
        return symbol + numberUtil.formatNumber(number);
      }
      return `- ${symbol}${numberUtil.formatNumber(Math.abs(number))}`;
    }
    return null;
  };

  const getSum = sumList => {
    let sum = 0;
    if (!sumList || isEmpty(sumList)) {
      return sum;
    }

    for (const amt of sumList) {
      if (amt) {
        sum = numberUtil.decimalAdd(sum, amt, 5);
      }
    }
    return sum;
  }

  const settingGridDatas = datas => {
    const demandQtySum = getSum(datas[DEMANDQTY]);
    const supplyQtySum = getSum(datas[SUPPLYQTY]);
    const gridData = [
      {
        category: PERIOD,
        rate: null,
        value: getPlanPeriod(datas?.mpVersion?.planConfigObj),
      },
      {
        category: DEMANDQTY,
        rate: null,
        value: demandQtySum,
      },
      {
        category: SUPPLYQTY,
        rate: null,
        value: supplyQtySum,
      },
      {
        category: PRODUCTIONQTY,
        rate: null,
        value: datas[PRODUCTIONQTY],
      },
      {
        category: "onhandQty",
        rate: null,
        value: datas[ONHANDQTY],
      },
    ];

    datas.rate = {};
    datas.rate[DEMANDQTY] = demandQtySum === 0 ? 0 : 100;
    datas.rate[SUPPLYQTY] = Math.round((supplyQtySum / demandQtySum) * 100);
    datas.rate[PRODUCTIONQTY] = Math.round((datas[PRODUCTIONQTY] / demandQtySum) * 100);
    datas.rate[ONHANDQTY] = Math.round((datas[ONHANDQTY] / demandQtySum) * 100);

    const includeList = [DEMANDQTY, SUPPLYQTY, ONHANDQTY, PRODUCTIONQTY];
    for (const [key, value] of Object.entries(datas)) {
      if (includes(key, includeList)) {
        const row = {};
        row.category = key;
        row.value = value;
        gridData.push(row);
      }
    }

    for (const rowData of gridData) {
      // 비율이 없는 행(기간·재고)은 null 로 둬야 스파크라인이 빈 칸으로 그려진다.
      rowData.rate = datas.rate[rowData.category] ?? null;
    }
    return gridData;
  };

  const gridDefs = [
    { headerName: "realCategory", field: "category", valueFormatter: ({ value }) => t(value) },
    {
      headerName: " ",
      field: "rate",
      valueGetter: ({ data }) => data.rate,
      cellRenderer: RatioBarCell,
    },
    {
      headerName: "value",
      field: "value",
      type: ["rightAligned", "numberColumn"],
      // numberColumn 기본 포맷터는 숫자가 아닌 값을 빈 문자열로 만든다. 기간 행은 문자열이라 그대로 통과시키고,
      // 나머지 행은 numberColumn 과 같은 옵션으로 포맷해 기존 표기를 유지한다.
      valueFormatter: params => {
        const { data, value } = params;
        if (isEqual(data?.category, PERIOD)) {
          return value ?? "";
        }
        if (isNil(value) || !isFinite(value)) {
          return "";
        }
        return numberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
      },
    },
  ];

  useEffect(() => {
    if (!isEmpty(data)) {
      setGridResultData(settingGridDatas(data));
      settingChartDatas(data, paramHeaders)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // 릴리즈 버튼은 화면 상단 공통 바로 빠졌다. 카드에는 상태만 남는다.
  // "릴리즈 중"도 상단 바가 보여 주므로 카드에서는 그리지 않는다.
  const ReleaseStatus = ({ version }) => {
    if (isEqual(version?.statusCd, RELEASED)) {
    return(
      <AppCard sx={{ display: "flex", width: "100%", height: "40px", minHeight: "40px", p: 0, mt: 1 }}>
        <CardMedia sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: green[700] }}>
          <Typography
            variant="subtitle1"
            pl="10px"
            color="#fff">
            {`${t("released")}`}
          </Typography>
        </CardMedia>
        <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
          <Typography
            variant="subtitle1"
            component="span"
            ml="auto"
            width="50%"
            textAlign="right"
            pr="10px">
            {`${dateUtils.formatDate(version.releaseTs, "yyyy-MM-dd")}`}
          </Typography>
        </Box>
      </AppCard>
    )} else if (isUnconfirmedScenarioVersion(version)) {
      // 확정 전 시나리오 플래닝 버전은 릴리즈할 수 없다. 확정 화면으로 보낸다.
      return <UnconfirmedScenarioNotice moduleCd="MP" />;
    }
    return null;
  }

  const getContextMenuItems = (params) => {
    return params.defaultItems.filter(
      (item) => item !== 'export'
    );
  };

  return (
    <Box sx={mainStyle}>
      <TitleCom
        planVersion={version}
        index={index}
        isReleasing={isReleasing}
        retrieveDeleteFlg={retrieveDeleteFlg}
      />
      <Box sx={{ width: "100%", height: "calc(60% - 100px)", flexShrink: 0 }}>
        <PivotChartComposed
          pivotConfig={pivotConfig}
          data={chartData}
          nullConvert
          options={{ minWidth: "33%", minHeight: 210, yAxisDomain: [0, dataMax] }}
          showConfigurationPopup={false}
        />
      </Box>
      <Box sx={{ height: "90px", flexShrink: 0 }}>
        <Box sx={{ width: "100%", height: "40px" }}>
          <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
            <CardMedia sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}>
              <Typography
                variant="subtitle1"
                pl="10px"
                color="#fff">
                {t("demandAmt")}
              </Typography>
            </CardMedia>
            <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
              <Typography
                variant="subtitle1"
                component="span"
                ml="auto"
                textAlign="right"
                pr="10px">
                {setCurrency(data?.demandAmt) ?? 0}
              </Typography>
            </Box>
          </AppCard>
        </Box>
        <Box sx={{ width: "100%", height: "40px", mt: 1 }}>
          <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
            <CardMedia sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}>
              <Typography
                variant="subtitle1"
                pl="10px"
                color="#fff">
                {t("supplyAmt")}
              </Typography>
            </CardMedia>
            <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
              <Typography
                variant="subtitle1"
                component="span"
                ml="auto"
                textAlign="right"
                pr="10px">
                {setCurrency(data?.supplyAmt) ?? 0}
              </Typography>
            </Box>
          </AppCard>
        </Box>
      </Box>
      <Box
        className="ag-theme-balham"
        sx={{ flex: 1, minHeight: 0, mt: 1 }}>
        <AgGridReact
          rowData={gridResultData}
          columnDefs={GridUtils.getColumnDefs(gridDefs)}
          {...DefaultGridSetting({ title })}
          defaultColDef={{
            flex: 1,
            minWidth: 30,
            suppressHeaderMenuButton: true,
            resizable: true,
          }}
          getContextMenuItems={getContextMenuItems}
          groupDefaultExpanded={-1}
          groupHideOpenParents
        />
      </Box>
      <ReleaseStatus version={version}/>
    </Box>
  );

};
const ScenarioComDashboard = ({ t, title }) => {
  const userData = JSON.parse(localStorage.getItem("user"));
  const symbol = userData.currencySymbol ?? "";

  const [loading, setLoading] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [scenarioDatas, setScenarioDatas] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [dataMax, setDataMax] = useState(0);
  const [dpVersions, setDpVersions] = useState([]);
  const [rpVersions, setRpVersions] = useState([]);
  const [userRoles, setUserRoles] = useState([]);

  const selectedVersion = useRef();

  // 카드에서 고른 버전 — 릴리즈는 상단 공통 바에서 이 버전 하나로만 실행한다.
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [warnDialogOpen, setWarnDialogOpen] = useState(false);
  const [warnContent, setWarnContent] = useState("");

  const selectedMpVersion = scenarioDatas?.find(scenarioData => isEqual(scenarioData?.mpVersion?.id, selectedCardId))?.mpVersion ?? null;
  const releasable = !!selectedMpVersion
    && !isEqual(selectedMpVersion.statusCd, RELEASED)
    && !isEqual(selectedMpVersion.statusCd, RELEASING)
    && !isUnconfirmedScenarioVersion(selectedMpVersion);

  // 버튼이 회색인 이유를 툴팁으로 알려 준다.
  const releaseBlockReason = !selectedMpVersion || releasable
    ? null
    : isUnconfirmedScenarioVersion(selectedMpVersion)
      ? t("MSG_ScenarioPlanningNotConfirmed")
      : t("MSG_NoVersionToRelease");

  const onClickReleaseButton = version => {
    selectedVersion.current = version;
    setReleaseOpen(true);
  };

  // 상위 모듈 버전이 아직 릴리즈되지 않았으면 먼저 확인받는다(권한 있는 모듈만 따진다).
  const handleReleaseClick = () => {
    if (!selectedMpVersion) {
      return;
    }
    const { planConfigObj } = selectedMpVersion;
    const dpVersion = includes(userRoles, roles.APP_DP) ? findVersionById(dpVersions, planConfigObj?.DP_VERSION_ID) : null;
    const rpVersion = includes(userRoles, roles.APP_RP) ? findVersionById(rpVersions, planConfigObj?.RP_VERSION_ID) : null;

    if (dpVersion && !isEqual(dpVersion.statusCd, RELEASED)) {
      setWarnContent(t("MSG_NotReleasedDpVersionConfirm"));
      setWarnDialogOpen(true);
    } else if (rpVersion && !isEqual(rpVersion.statusCd, RELEASED)) {
      setWarnContent(t("MSG_NotReleasedRpVersionConfirm"));
      setWarnDialogOpen(true);
    } else {
      onClickReleaseButton(selectedMpVersion);
    }
  };

  const handleWarnDialog = action => {
    if (action === "WARNING") {
      onClickReleaseButton(selectedMpVersion);
    }
  };

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: userData.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };

  const retrieveDeleteFlg = version => {
    const planVersionId = { planVersionId: version?.id };
    planVersionService.delFlg(planVersionId)
      .then(response => {
        const updatedVersion = response.data;

        const datas = [];
        for (const data of scenarioDatas) {
          if (isEqual(updatedVersion.id, data?.mpVersion?.id)) {
            data.mpVersion = updatedVersion;
          }
          datas.push(data);
        }

        setScenarioDatas(datas);
      })
      .catch(e => {
        if (e.response.data) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t(e.response.data) });
        }
        debug(e);
      });
  };

  const sortBySupplyAmt = list => {
    list.sort((a, b) => {
      if (a.mpVersion?.statusCd !== b.mpVersion?.statusCd) {
        return b.mpVersion?.statusCd.localeCompare(a.mpVersion?.statusCd);
      } else {
        if (a.supplyAmt > b.supplyAmt) {
          return -1;
        }
        if (a.supplyAmt < b.supplyAmt) {
          return 1;
        }
        return 0;
      }
    });
    return list;
  };

  const retrieveMpScenario = () => {
    mpScenarioComparsionService.getAll()
      .then(response => {
        if (response.data) {
          if (response?.data?.message) {
            const { message } = response.data;
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            const scenarios = response?.data?.scenarios ?? [];
            if (size(scenarios) > 0) {

              const hasReleasingVersion = scenarios.some(
                scenario => isEqual(scenario?.mpVersion?.statusCd, RELEASING)
              );
              setIsReleasing(hasReleasingVersion);

              setScenarioDatas(sortBySupplyAmt(scenarios));
              const arrays = [];
              for (const scenario of scenarios) {
                if (scenario?.[DEMANDQTY]) arrays.push(...scenario[DEMANDQTY]);
                if (scenario?.[SUPPLYQTY]) arrays.push(...scenario[SUPPLYQTY]);
              }
              const maxValue = numberUtil.getMaxValueForChart(arrays);
              setDataMax(maxValue);
            }
          }
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoVersion") });
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        debug(e);
      });
  };

  const setVersion = version => {
    if (!isUndefined(version)) {
      selectedVersion.current = version;
    }
  };

  const retrieveDpVersion = () => {
    planVersionService.getAll({ moduleCd: DP, statusCds: [RELEASED, SCENARIO], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        setDpVersions(results);
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveRpVersion = () => {
    planVersionService.getAll({ moduleCd: RP, statusCds: [RELEASED, SIMULATION, PLANNED], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        setRpVersions(results);
      })
      .catch(e => {
        debug(e);
      });
  };

  useEffect(() => {
    retrieveUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    retrieveDpVersion();
    retrieveRpVersion();
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles]);

  useEffect(() => {
    if (loading && userRoles) {
      retrieveMpScenario();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <>
      <Box className={"wrap-comparion-dashboard display-block"}>
          <ComparisonReleaseBar
            selectedName={selectedMpVersion?.name}
            disabled={!releasable}
            disabledReason={releaseBlockReason}
            isReleasing={isReleasing}
            onRelease={handleReleaseClick}
          />
          <comparisonComponent.ComparisonContainer>
            {
              scenarioDatas?.map((scenarioData, idx) => (
                <SwiperSlide key={scenarioData?.mpVersion?.id}>
                  <SelectableComparison
                    selected={isEqual(scenarioData?.mpVersion?.id, selectedCardId)}
                    onSelect={() => setSelectedCardId(prev => (isEqual(prev, scenarioData?.mpVersion?.id) ? null : scenarioData?.mpVersion?.id))}>
                    <Comparison
                      title={title}
                      symbol={symbol}
                      data={scenarioData}
                      index={idx}
                      dataMax={dataMax}
                      retrieveDeleteFlg={retrieveDeleteFlg}
                      isReleasing={isReleasing}
                    />
                  </SelectableComparison>
                </SwiperSlide>
              ))
            }
          </comparisonComponent.ComparisonContainer>
      </Box>
      <Dialog
        open={warnDialogOpen}
        title="warning"
        content={warnContent}
        onHandler={handleWarnDialog}
        onClose={() => setWarnDialogOpen(false)}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <ScenarioReleaseModal
        open={releaseOpen}
        close={setReleaseOpen}
        selectScenario={selectedVersion?.current ?? {}}
        service={mpScenarioComparsionService}
        moduleCd={MP}
        reload={retrieveMpScenario}
        setIsReleasing={setIsReleasing}
        releaseVersion={setVersion}
      />
    </>
  );
};

export default withTranslation()(ScenarioComDashboard);
